import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import debounce from 'lodash.debounce'
import { BasicSlider } from '../BasicSlider/BasicSlider'

/**
 * The CardsSlider provides a space-saving way for adding an arbitrary
 * amount of cards. If the amount of cards exceeds the horizontal space,
 * a carousel slider is automatically created. This gives the user
 * the possibility to reach all available cards by sliding horizontally.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-cards-slider.html
 *
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {('left'|'center'|'right')} [cellAlign='left'] - Align cells within the {@link CardsSlider} component.
 * @param {(boolean|number|string)} [groupCells=true] - Group cells that fit into the {@link CardsSlider} viewport.
 * @param {CardsSliderItem[]} [children] - A collection of {@link CardsSliderItem} components.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The {@link CardsSlider} component.
 * @constructor
 */
export function CardsSlider({ className, cellAlign = 'left', groupCells = true, children, testId, ...otherProps }) {
  const initialResizeThreshold = 300
  const [flickityRef, setFlickityRef] = useState(null)

  /**
   * Handle {@link BasicSlider} side-effects.
   * @TODO: Should be extracted into a generic FlickityExtension, @see BRON-8286.
   */
  useEffect(() => {
    const instance = flickityRef
    if (instance) {
      const resizeFn = instance.resize.bind(instance)

      instance.resize = () => {
        instance.element.classList.remove('flickity-resize')
        resizeFn()
        requestAnimationFrame(() => instance.element.classList.add('flickity-resize'))
      }

      const onReady = () => {
        /**
         * Fixes incorrect height calculation.
         * Trigger resize after all fonts are loaded.
         */
        document.fonts.ready.then(() => {
          debounce(instance.resize, initialResizeThreshold)()
        })

        /**
         * Fixes nasty layout bug for some journeys.
         * The {@link initialResizeThreshold} must be increased for this to work.
         * @see https://jira.platform.vwfs.io/browse/BRON-7534
         */
        const timeout = setTimeout(() => {
          resizeFn()
        }, initialResizeThreshold + 50)
        return () => {
          timeout && clearTimeout(timeout)
        }
      }

      instance.on('select', toggleSliderControls)
      instance.on('ready', onReady)

      return () => {
        instance.off('select', toggleSliderControls)
        instance.off('ready', onReady)
      }
    }
    return () => {}
  }, [flickityRef, toggleSliderControls])

  /**
   * Handle {@link BasicSlider} when children change dynamically.
   * @TODO: Should be extracted into a generic FlickityExtension, @see BRON-8286.
   */
  useEffect(() => {
    if (flickityRef) {
      /**
       * Resize Flickity and recalculate the position of all children.
       * @see https://flickity.metafizzy.co/api.html#reloadcells
       * @see https://flickity.metafizzy.co/api.html#resize
       */
      flickityRef.reloadCells()
      flickityRef.resize()
    }
  }, [flickityRef, children])

  /**
   * Handle {@link BasicSlider} controls.
   * @TODO: Should be extracted into a generic FlickityExtension, @see BRON-8286.
   */
  const toggleSliderControls = useCallback(() => {
    if (flickityRef) {
      const size = flickityRef.size.width
      const { slideableWidth } = flickityRef
      const slideable = slideableWidth > size

      const pageDotsHTMLCollection = flickityRef.element.getElementsByClassName('flickity-page-dots')
      const flickityButtonsHTMLCollection = flickityRef.element.getElementsByClassName('flickity-prev-next-button')

      /**
       * This fixes a bug where Flickity is unable to reassign the draggable
       * behavior when resizing from a big to small viewport when `groupCells`
       * or `contain` options are set. The fix only kicks in when the `draggable`
       * option in Flickity was set.
       * @see https://flickity.metafizzy.co/options.html#draggable
       * @see https://github.com/metafizzy/flickity/issues/960
       */
      if (flickityRef.options.draggable) {
        flickityRef.isDraggable = slideable
        flickityRef.element.classList.toggle('is-draggable', slideable)
        flickityRef.element.classList.toggle('is-slideable', slideable)
      }

      Array.from(pageDotsHTMLCollection).forEach((pageDots) => {
        pageDots.classList.toggle('is-slideable', slideable)
      })

      Array.from(flickityButtonsHTMLCollection).forEach((btn) => {
        btn.classList.toggle('is-slideable', slideable)
      })
    }
  }, [flickityRef])

  const flickityOptions = {
    cellSelector: '.js-slider__item',
    pageDots: true,
    prevNextButtons: true,
    groupCells,
    cellAlign,
    contain: false,
    freeScroll: true,
  }

  const classNameList = classNames('c-cards-slider-container', 'js-slider', className).trim()
  const flickityClassNameList = classNames('c-cards-slider', 'js-slider__element').trim()

  return (
    <div className={classNameList} data-testid={testId} {...otherProps}>
      <BasicSlider
        elementType="div"
        className={flickityClassNameList}
        flickityRef={(c) => setFlickityRef(c)}
        options={flickityOptions}
        onReady={(c) => c?.element?.parentElement?.classList.add('is-initialized')}
      >
        {children}
      </BasicSlider>
    </div>
  )
}

CardsSlider.propTypes = {
  className: PropTypes.string, // Additional CSS classes on the component element.
  cellAlign: PropTypes.oneOf(['left', 'center', 'right']), // Bronson template: 'alignCenter'.
  groupCells: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]), // Bronson template: 'individual'.
  children: PropTypes.node, // Bronson template: 'cards-slider-items'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

/**
 * The CardsSliderItem component is internally used to wrap
 * the item content and display it inside the {@link CardsSlider}.
 *
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {React.ReactNode} [children] - The content to display inside the {@link CardsSliderItem} component.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The {@link CardsSliderItem} component.
 * @constructor
 */
function CardsSliderItem({ className, children, testId, ...otherProps }) {
  const classNameList = classNames('c-cards-slider__item', 'js-slider__item', className).trim()

  return (
    <div className={classNameList} data-testid={testId} {...otherProps}>
      <div className="c-cards-slider__card-wrapper">{children}</div>
    </div>
  )
}

CardsSliderItem.propTypes = {
  className: PropTypes.string, // Additional CSS classes on the component element.
  children: PropTypes.node, // Bronson template: 'card'. Use {@link Card} component.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

CardsSliderItem.displayName = 'CardsSlider.Item'
CardsSlider.Item = CardsSliderItem
